// src/components/Pages/SecurityCenter.jsx  →  /security
import React from 'react';
import { Link } from 'react-router-dom';
import { FiShield, FiLock, FiUserCheck, FiActivity, FiServer, FiKey, FiArrowLeft, FiArrowRight,
  FiSlash, FiCheckCircle, FiAlertTriangle, FiBell, FiFlag } from 'react-icons/fi';
import { useAuthGate } from '../../Context/AuthGateContext';
import PhaseRoadmap from './PhaseRoadmap';
import WalletSecurityFaq from '../WalletSecurity/WalletSecurityFaq';
import './InfoPages.css';

const FEATURES = [
  { Icon: FiUserCheck, title: 'Solo vendedores verificados', text: 'Nadie vende sin ser un comerciante aprobado por invitación. El público general solo compra.' },
  { Icon: FiServer,    title: 'No custodiamos tus fondos',   text: 'Bitplify es un mercado P2P: vos controlás tus activos, no un tercero.' },
  { Icon: FiBell,      title: 'Alerta de inicio de sesión',  text: 'Si alguien entra a tu cuenta desde un dispositivo nuevo, te avisamos por correo al instante.' },
  { Icon: FiAlertTriangle, title: 'Aviso de pagos reversibles', text: 'Marcamos con ⚠️ los métodos que se pueden revertir (PayPal, Zelle, Mercado Pago) para que el vendedor no se lleve un contracargo.' },
  { Icon: FiFlag,      title: 'Bloquear y reportar',         text: 'Con un toque desde el perfil de cualquier trader podés bloquearlo y reportarlo a moderación.' },
  { Icon: FiShield,    title: 'Disputas asistidas',          text: 'Si un trade se tuerce, un moderador revisa la evidencia (chat + comprobantes) de ambas partes.' },
  { Icon: FiActivity,  title: 'Reputación pública',          text: 'Cada trader muestra sus ratings y trades completados: la confianza se gana, no se inventa.' },
  { Icon: FiLock,      title: 'Cifrado SSL / TLS',           text: 'Todo el tráfico viaja cifrado de extremo a extremo (HTTPS 256-bit).' },
];

// Reglas de oro para no caer en estafas — cada una atada a una función real de Bitplify.
const RULES = [
  { Icon: FiSlash,        title: 'Nunca operes por fuera de Bitplify',        text: 'Si te piden pasar a WhatsApp o Telegram, o pagar “directo” por afuera, es la estafa nº1. Todo —chat, comprobantes y disputas— tiene que quedar dentro de la plataforma.' },
  { Icon: FiCheckCircle,  title: 'Confirmá el dinero en TU cuenta antes de liberar', text: 'Una captura o un comprobante NO es un pago. Recién cuando ves la plata realmente acreditada en tu banco o billetera, liberás la cripto.' },
  { Icon: FiAlertTriangle, title: 'Cuidado con los pagos reversibles',        text: 'PayPal, Zelle y Mercado Pago se pueden revertir con un contracargo. Los marcamos con ⚠️ en cada oferta: como vendedor, priorizá métodos irreversibles y contrapartes con reputación.' },
  { Icon: FiKey,          title: 'Bitplify nunca te pide tu clave ni tu frase de recuperación', text: 'Ni soporte, ni un correo, ni un “moderador”. Si alguien te la pide, es phishing. Tu frase de recuperación no se comparte con NADIE, jamás.' },
  { Icon: FiUserCheck,    title: 'Mirá reputación y verificación',            text: 'Operá con vendedores verificados, con ratings y trades completados. La insignia de verificado y el historial público están para que no compres a ciegas.' },
  { Icon: FiBell,         title: 'Prendé la verificación de email',           text: 'Con tu email verificado recibís las alertas de seguridad: te avisamos de cada inicio de sesión desde un dispositivo nuevo y de cualquier cambio de contraseña. Si ves uno que no reconocés, actuá ya.' },
  { Icon: FiFlag,         title: 'Ante la duda, bloqueá y reportá',           text: 'Si alguien te apura, te presiona o actúa raro, no sigas: bloquealo y reportalo desde su perfil. Preferimos una operación menos a una estafa más.' },
  { Icon: FiShield,       title: 'Si algo se tuerce, abrí una disputa',       text: 'No intentes arreglarlo por afuera. Abrí la disputa dentro del trade y un moderador revisa la evidencia de ambas partes para resolver.' },
];

const PHASES = [
  { kicker: 'Fase 0 · Ahora', status: 'active', title: 'MVP — Comunidad verificada',
    desc: 'La seguridad de hoy: controlamos quién puede vender.',
    items: ['Solo vendedores verificados por invitación pueden vender', 'El público general únicamente compra', 'Cifrado SSL/TLS en toda la app', 'Reputación y ratings públicos de traders', 'Recolección básica de datos en el registro'] },
  { kicker: 'Fase 1 · En curso', status: 'progress', title: 'Verificación básica (KYC Nivel 1)',
    items: ['Verificación de email', 'Alerta de inicio de sesión desde dispositivos nuevos', 'Código OTP por teléfono', 'Protección anti-bot (Cloudflare Turnstile)'] },
  { kicker: 'Fase 2 · Próximamente', status: 'soon', title: 'Identidad verificada (KYC Nivel 2)',
    items: ['Documento + selfie con proveedor certificado', 'Venta abierta a cualquier verificado (sin invitación)', 'Screening AML / listas de sanciones'] },
  { kicker: 'Fase 3 · Planeado', status: 'planned', title: 'Custodia y escrow protegido',
    items: ['Escrow: fondos retenidos hasta confirmar el pago', 'Centro de disputas y resolución asistida', 'Monitoreo de transacciones en tiempo real'] },
];

export default function SecurityCenter() {
  const { openAuth } = useAuthGate();
  return (
    <div className="ip-wrap">
      <Link to="/" className="ip-back"><FiArrowLeft /> Volver al marketplace</Link>

      <div className="ip-hero">
        <span className="ip-eyebrow"><FiShield /> Centro de seguridad</span>
        <h1 className="ip-title">Tu seguridad, por fases</h1>
        <p className="ip-sub">Estamos construyendo Bitplify de forma transparente. Esto es lo que ya te protege hoy y lo que viene en cada fase.</p>
        <span className="ip-phase-badge"><span className="ip-live-dot" /> Estás en la fase MVP (Beta)</span>
      </div>

      <div className="ip-note ip-note-accent">
        <span className="ip-note-ic"><FiUserCheck /></span>
        <p><strong>Hoy, esto es lo que te protege:</strong> en Bitplify <strong>solo pueden vender comerciantes verificados por invitación</strong>. El público general únicamente compra. Así nos aseguramos de que del otro lado siempre haya alguien aprobado y con reputación — no un desconocido que vende, cobra y desaparece.</p>
      </div>

      <section className="ip-section">
        <h2 className="ip-section-title"><FiUserCheck /> Cómo te protegemos hoy</h2>
        <div className="ip-grid">
          {FEATURES.map(({ Icon, title, text }) => (
            <div key={title} className="ip-card">
              <div className="ip-card-ic"><Icon /></div>
              <h3 className="ip-card-title">{title}</h3>
              <p className="ip-card-text">{text}</p>
            </div>
          ))}
        </div>
      </section>

      <section className="ip-section">
        <h2 className="ip-section-title"><FiShield /> Cómo evitar estafas</h2>
        <p className="ip-sub" style={{ marginTop: '-0.25rem', marginBottom: '1.1rem' }}>
          Las reglas de oro para operar tranquilo. Cada una se apoya en una función real de Bitplify.
        </p>
        <div className="ip-grid">
          {RULES.map(({ Icon, title, text }) => (
            <div key={title} className="ip-card">
              <div className="ip-card-ic"><Icon /></div>
              <h3 className="ip-card-title">{title}</h3>
              <p className="ip-card-text">{text}</p>
            </div>
          ))}
        </div>
      </section>

      <section className="ip-section">
        <h2 className="ip-section-title"><FiKey /> Tu wallet, sin miedo</h2>
        <p className="ip-sub" style={{ marginTop: '-0.25rem', marginBottom: '1.1rem' }}>
          Sabemos que las wallets dan respeto. Acá te respondemos en simple lo que casi nadie te explica.
        </p>
        <WalletSecurityFaq />
      </section>

      <section className="ip-section">
        <h2 className="ip-section-title"><FiActivity /> Roadmap de seguridad</h2>
        <PhaseRoadmap phases={PHASES} />
      </section>

      <div className="ip-cta">
        <h3>Tu plata, tu llave — desde el día uno</h3>
        <p>Creá tu cuenta gratis y operá P2P con autocustodia: nadie controla tus fondos más que vos.</p>
        <button className="ip-cta-btn" onClick={() => openAuth('register')}>Crear mi cuenta <FiArrowRight /></button>
      </div>
    </div>
  );
}
